import { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { resourceTeamsUrl } from '../../configurations/api/url';
import { ROUTES } from '../../types/global/routes.types';
import { replaceRouteParam } from '../../utils/routesProcessor';
import { showToastMessage } from '../../utils/AppUtils';
import { BRAND_LUXE_PRIMARY } from '../../pages/BrandDashboard/brandDashboardTokens';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../constants/obsidianDashboardTheme';
import {
    editorialHeadlineSx,
    editorialLabelSx,
    editorialSelectProps,
    midnight,
} from '../../pages/BrandDashboard/contestWizard/midnightStyles';

export type ResourceTeamKind = 'contest' | 'audition' | 'campaign';

type Member = {
    id: string;
    email: string;
    role: string;
    status: string;
};

type Activity = {
    id: string;
    activity_type: string;
    description: string;
    created_at: string;
    actor?: { firstName?: string; lastName?: string } | null;
};

type TeamPayload = {
    access?: {
        role?: string;
        can_manage_team?: boolean;
        seats_used?: number;
        seat_limit?: number | null;
    };
    members?: Member[];
    activity?: Activity[];
    activity_pagination?: {
        page: number;
        limit: number;
        total: number;
        total_pages: number;
    };
};

const ACTIVITY_PAGE_SIZE = 8;

const teamFieldSx = {
    '& .MuiOutlinedInput-root': {
        height: 44,
        bgcolor: OBSIDIAN.surfaceContainerHighest,
        borderRadius: '12px',
        color: OBSIDIAN.onSurface,
        fontFamily: OBSIDIAN_BODY_FONT,
        fontWeight: 600,
        '& fieldset': { border: `1px solid ${OBSIDIAN.primary}88` },
        '&:hover fieldset': { border: `1px solid ${OBSIDIAN.primary}` },
        '&.Mui-focused fieldset': { border: `1px solid ${OBSIDIAN.primary}` },
        '&.Mui-focused': { boxShadow: 'none' },
    },
    '& .MuiInputBase-input': {
        color: OBSIDIAN.onSurface,
        fontFamily: OBSIDIAN_BODY_FONT,
        fontWeight: 600,
    },
    '& .MuiInputBase-input::placeholder': {
        color: OBSIDIAN.onSurfaceVariant,
        opacity: 1,
        fontWeight: 600,
    },
    '& .MuiSelect-select': {
        color: OBSIDIAN.onSurface,
        fontFamily: OBSIDIAN_DISPLAY_FONT,
        fontWeight: 800,
    },
    '& .MuiSelect-icon': { color: OBSIDIAN.primary },
};

function hrefFor(kind: ResourceTeamKind, id: string) {
    if (kind === 'contest') {
        return replaceRouteParam(ROUTES.BRAND_CONTEST_MANAGE, 'contestId', id);
    }
    if (kind === 'audition') {
        return replaceRouteParam(ROUTES.BRAND_AUDITION_DETAIL, 'auditionId', id);
    }
    return replaceRouteParam(ROUTES.BRAND_CAMPAIGN_DETAIL, 'campaignId', id);
}

export function ResourceTeamPanel({
    resourceType,
    resourceId,
}: {
    resourceType: ResourceTeamKind;
    resourceId: string;
}) {
    const token = useSelector((s: any) => s.authentication.token as string);
    const [data, setData] = useState<TeamPayload | null>(null);
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('editor');
    const [busy, setBusy] = useState(false);
    const [activityPage, setActivityPage] = useState(1);

    const load = useCallback(
        async (page = 1) => {
            if (!token || !resourceId) return;
            const res = await axios.get(`${resourceTeamsUrl}/${resourceType}/${resourceId}`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { activity_page: page, activity_limit: ACTIVITY_PAGE_SIZE },
            });
            setData(res.data?.data || null);
            setActivityPage(page);
        },
        [token, resourceType, resourceId],
    );

    useEffect(() => {
        void load().catch(() => setData(null));
    }, [load]);

    const invite = async () => {
        setBusy(true);
        try {
            await axios.post(
                `${resourceTeamsUrl}/${resourceType}/${resourceId}`,
                { email: email.trim(), role },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setEmail('');
            showToastMessage('Invite sent', 'success');
            await load(1);
        } catch (err: any) {
            showToastMessage(err?.response?.data?.message || 'Could not invite', 'error');
        } finally {
            setBusy(false);
        }
    };

    const remove = async (memberId: string) => {
        setBusy(true);
        try {
            await axios.delete(`${resourceTeamsUrl}/${resourceType}/${resourceId}/${memberId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            await load(activityPage);
        } catch (err: any) {
            showToastMessage(err?.response?.data?.message || 'Could not remove', 'error');
        } finally {
            setBusy(false);
        }
    };

    const access = data?.access;
    const pages = data?.activity_pagination;
    const seatLabel =
        access?.seat_limit == null ? 'Unlimited seats' : `${access?.seats_used ?? 0} / ${access.seat_limit} seats`;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
                <Typography sx={{ ...editorialLabelSx, color: OBSIDIAN.primary }}>Access</Typography>
                <Typography
                    sx={{
                        mt: 1,
                        fontFamily: OBSIDIAN_BODY_FONT,
                        fontSize: 15,
                        fontWeight: 600,
                        lineHeight: 1.5,
                        color: OBSIDIAN.onSurface,
                    }}>
                    {access?.role ? `You are ${access.role}. ` : ''}
                    {seatLabel}. Admin can edit and invite. Editor can change content. Viewer is read-only.
                </Typography>
            </Box>

            {access?.can_manage_team ? (
                <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        sx={{ ...teamFieldSx, flex: 1, minWidth: 200 }}
                    />
                    <TextField
                        select
                        size="small"
                        value={role}
                        onChange={e => setRole(e.target.value)}
                        SelectProps={editorialSelectProps}
                        sx={{ ...teamFieldSx, width: 148 }}>
                        {access.role === 'owner' ? <MenuItem value="admin">Admin</MenuItem> : null}
                        <MenuItem value="editor">Editor</MenuItem>
                        <MenuItem value="viewer">Viewer</MenuItem>
                    </TextField>
                    <Button
                        disabled={busy || !email.trim()}
                        onClick={() => void invite()}
                        sx={{
                            height: 40,
                            px: 2.5,
                            borderRadius: 999,
                            textTransform: 'none',
                            fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                            fontWeight: 800,
                            fontSize: 13,
                            color: '#392700',
                            bgcolor: BRAND_LUXE_PRIMARY,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#feb700', boxShadow: 'none' },
                            '&.Mui-disabled': { bgcolor: 'rgba(255,201,101,0.35)', color: 'rgba(57,39,0,0.5)' },
                        }}>
                        {busy ? <CircularProgress size={16} sx={{ color: '#392700' }} /> : 'Invite'}
                    </Button>
                </Box>
            ) : null}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(data?.members || []).length === 0 ? (
                    <Typography sx={{ ...editorialLabelSx, letterSpacing: '0.08em' }}>No teammates yet</Typography>
                ) : null}
                {(data?.members || []).map(member => (
                    <Box
                        key={member.id}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1.5,
                            borderRadius: '12px',
                            bgcolor: midnight.surfaceHighest,
                            border: '1px solid rgba(255,255,255,0.06)',
                        }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                                    fontWeight: 700,
                                    fontSize: 14,
                                    color: midnight.onSurface,
                                }}>
                                {member.email}
                            </Typography>
                            <Typography sx={{ ...editorialLabelSx, mt: 0.5, letterSpacing: '0.12em' }}>
                                {member.role} · {member.status}
                            </Typography>
                        </Box>
                        {access?.can_manage_team ? (
                            <Button
                                size="small"
                                onClick={() => void remove(member.id)}
                                sx={{
                                    textTransform: 'none',
                                    fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                                    fontWeight: 700,
                                    color: '#ffb4ab',
                                }}>
                                Remove
                            </Button>
                        ) : null}
                    </Box>
                ))}
            </Box>

            <Box>
                <Typography sx={editorialLabelSx}>Activity</Typography>
                <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {(data?.activity || []).length === 0 ? (
                        <Typography
                            sx={{
                                fontFamily: '"Manrope", Inter, sans-serif',
                                fontSize: 13,
                                color: midnight.onSurfaceVariant,
                            }}>
                            Invites, accepts, and role changes show up here.
                        </Typography>
                    ) : null}
                    {(data?.activity || []).map(row => {
                        const name = [row.actor?.firstName, row.actor?.lastName].filter(Boolean).join(' ') || 'Someone';
                        return (
                            <Box
                                key={row.id}
                                sx={{
                                    px: 2,
                                    py: 1.25,
                                    borderRadius: '12px',
                                    bgcolor: midnight.surfaceLow,
                                }}>
                                <Typography
                                    sx={{
                                        fontFamily: '"Manrope", Inter, sans-serif',
                                        fontSize: 13,
                                        color: midnight.onSurface,
                                    }}>
                                    {name} · {row.description || row.activity_type}
                                </Typography>
                                <Typography sx={{ ...editorialLabelSx, mt: 0.5, letterSpacing: '0.08em' }}>
                                    {new Date(row.created_at).toLocaleString()}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
                {pages && pages.total_pages > 1 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5 }}>
                        <Button
                            size="small"
                            disabled={activityPage <= 1}
                            onClick={() => void load(activityPage - 1)}
                            sx={{
                                color: OBSIDIAN.primary,
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 800,
                                textTransform: 'none',
                                '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)' },
                            }}>
                            Previous
                        </Button>
                        <Typography
                            sx={{
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontWeight: 700,
                                fontSize: 13,
                                color: OBSIDIAN.onSurfaceVariant,
                            }}>
                            {pages.page} / {pages.total_pages}
                        </Typography>
                        <Button
                            size="small"
                            disabled={activityPage >= pages.total_pages}
                            onClick={() => void load(activityPage + 1)}
                            sx={{
                                color: OBSIDIAN.primary,
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 800,
                                textTransform: 'none',
                                '&.Mui-disabled': { color: 'rgba(255,255,255,0.25)' },
                            }}>
                            Next
                        </Button>
                    </Box>
                ) : null}
            </Box>
        </Box>
    );
}

type SharedRow = {
    id: string;
    resource_type: ResourceTeamKind;
    resource_id: string;
    title: string;
    role: string;
    status: string;
};

export function BrandSharedResources() {
    const token = useSelector((s: any) => s.authentication.token as string);
    const [searchParams, setSearchParams] = useSearchParams();
    const [rows, setRows] = useState<SharedRow[]>([]);
    const [acceptingId, setAcceptingId] = useState<string | null>(null);
    const nav = useNavigate();

    const load = useCallback(async () => {
        if (!token) return;
        const res = await axios.get(`${resourceTeamsUrl}/mine`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    }, [token]);

    const acceptRow = async (id: string) => {
        setAcceptingId(id);
        try {
            await axios.post(`${resourceTeamsUrl}/accept`, { id }, { headers: { Authorization: `Bearer ${token}` } });
            showToastMessage('Invite accepted', 'success');
            await load();
        } catch (err: any) {
            showToastMessage(err?.response?.data?.message || 'Could not accept invite', 'error');
        } finally {
            setAcceptingId(null);
        }
    };

    useEffect(() => {
        const invite = searchParams.get('team_invite');
        if (!token || !invite) {
            void load().catch(() => setRows([]));
            return;
        }
        axios
            .post(`${resourceTeamsUrl}/accept`, { token: invite }, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => {
                showToastMessage('Invite accepted', 'success');
                const next = new URLSearchParams(searchParams);
                next.delete('team_invite');
                setSearchParams(next, { replace: true });
                const accepted = res.data?.data;
                if (accepted?.resource_id && accepted?.resource_type) {
                    nav(hrefFor(accepted.resource_type as ResourceTeamKind, String(accepted.resource_id)), {
                        replace: true,
                    });
                }
            })
            .catch((err: any) => {
                showToastMessage(err?.response?.data?.message || 'Could not accept invite', 'error');
            })
            .finally(() => {
                void load().catch(() => setRows([]));
            });
    }, [token, searchParams, setSearchParams, load, nav]);

    if (!rows.length) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={editorialLabelSx}>Shared with you</Typography>
            {rows.map(row => {
                const pending = row.status === 'pending';
                return (
                    <Box
                        key={row.id}
                        component={pending ? 'div' : Link}
                        to={pending ? undefined : hrefFor(row.resource_type, String(row.resource_id))}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1.5,
                            textDecoration: 'none',
                            px: 2,
                            py: 1.5,
                            borderRadius: '12px',
                            bgcolor: OBSIDIAN.surfaceContainerHighest,
                            border: `1px solid ${OBSIDIAN.primary}88`,
                            borderLeft: `4px solid ${OBSIDIAN.primary}`,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
                            color: OBSIDIAN.onSurface,
                        }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ ...editorialHeadlineSx, fontSize: 15 }}>{row.title}</Typography>
                            <Typography
                                sx={{
                                    ...editorialLabelSx,
                                    mt: 0.75,
                                    letterSpacing: '0.12em',
                                    color: OBSIDIAN.primary,
                                }}>
                                {row.resource_type} · {row.role} · {row.status}
                            </Typography>
                        </Box>
                        {pending ? (
                            <Button
                                disabled={acceptingId === row.id}
                                onClick={() => void acceptRow(row.id)}
                                sx={{
                                    flexShrink: 0,
                                    textTransform: 'none',
                                    fontFamily: OBSIDIAN_DISPLAY_FONT,
                                    fontWeight: 800,
                                    color: OBSIDIAN.primary,
                                    '&.Mui-disabled': { color: 'rgba(255,201,101,0.4)' },
                                }}>
                                Accept
                            </Button>
                        ) : null}
                    </Box>
                );
            })}
        </Box>
    );
}
